'use client';

import { useEffect, useRef, useState } from 'react';

const YT_API_SCRIPT_ID = 'yt-iframe-api';

function extractVideoId(url) {
    if (!url) return null;
    return url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?\s]+)/)?.[1] ?? null;
}

function formatDuration(seconds) {
    if (!seconds || Number.isNaN(seconds)) return '';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}m ${s}s`;
}

function loadYouTubeAPI(callback) {
    if (typeof window === 'undefined') return;

    if (window.YT?.Player) {
        callback();
        return;
    }

    const existing = window.__ytApiCallbacks ?? [];
    existing.push(callback);
    window.__ytApiCallbacks = existing;

    if (!document.getElementById(YT_API_SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = YT_API_SCRIPT_ID;
        script.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(script);
    }

    window.onYouTubeIframeAPIReady = () => {
        (window.__ytApiCallbacks ?? []).forEach((cb) => cb());
        window.__ytApiCallbacks = [];
    };
}

export default function YouTubeEmbed({ url, className = '', onDuration, autoPlay = false }) {
    const containerRef = useRef(null);
    const [isReady, setIsReady] = useState(false);
    const videoId = extractVideoId(url);

    useEffect(() => {
        if (!videoId || !containerRef.current) return;

        let player;
        let cancelled = false;

        const init = () => {
            if (cancelled || !containerRef.current) return;
            player = new window.YT.Player(containerRef.current, {
                videoId,
                playerVars: {
                    rel: 0,
                    autoplay: autoPlay ? 1 : 0,
                    playsinline: 1
                },
                events: {
                    onReady: (e) => {
                        setIsReady(true);
                        const seconds = e.target.getDuration();
                        const formatted = formatDuration(seconds);
                        if (formatted) onDuration?.(formatted);
                    }
                }
            });
        };

        loadYouTubeAPI(init);

        return () => {
            cancelled = true;
            try {
                player?.destroy?.();
            } catch (_) {
                /* no-op */
            }
        };
    }, [videoId, autoPlay, onDuration]);

    if (!videoId) return null;

    return (
        <div className={`relative overflow-hidden ${className}`}>
            {!isReady && <div className="absolute inset-0 animate-pulse bg-white/10" />}
            <div ref={containerRef} className="size-full" />
        </div>
    );
}
